import { useState } from 'react'
import { Dialog } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined'
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { formatCLPFull } from '../utils/visionDelNegocio.utils'

export const InitialBalancesModal = ({ isCompact, controller }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const {
    openInitialBalanceModal,
    closeInitialBalanceModal,
    initialBalancesLoading,
    hasInitialBalances,
    initialBalanceTotal,
    initialBalanceRows,
    isSavingInitialBalance,
    deletingInitialBalanceId,
    editingInitialBalanceId,
    handleEditInitialBalance,
    handleDeleteInitialBalance,
    isEditingInitialBalance,
    handleSubmitInitialBalance,
    initialBalanceForm,
    handleChangeInitialBalanceForm,
    bankCatalog,
    banksLoading,
    formatInitialBalanceCurrencyInput,
    initialBalanceDrafts,
    handleChangeInitialBalanceDraft,
    handleRemoveInitialBalanceDraft,
    handleAddInitialBalanceDraft,
    handleCancelInitialBalanceEdit
  } = controller

  return (
    <Dialog
      open={openInitialBalanceModal}
      onClose={closeInitialBalanceModal}
      fullScreen={isCompact}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: isExpanded ? '96vw' : 760 },
          height: { xs: '100%', md: isExpanded ? '90vh' : 'auto' },
          maxWidth: { xs: '100%', md: '96vw' },
          maxHeight: { xs: '100%', md: '90vh' },
          borderRadius: { xs: 0, md: 4 },
          overflow: 'hidden'
        }
      }}
    >
      <div className='flex h-full min-h-0 flex-col bg-surface'>
        <div className='flex items-center justify-end bg-primary px-4 py-2'>
          <div className='flex items-center gap-1'>
            {!isCompact && (
              <button
                type='button'
                onClick={() => setIsExpanded(prev => !prev)}
                aria-label={isExpanded ? 'Restaurar tamano' : 'Agrandar modal'}
                title={isExpanded ? 'Restaurar tamano' : 'Agrandar modal'}
                className='inline-flex h-7 w-7 items-center justify-center rounded text-surface transition hover:bg-surface/15'
              >
                <CropSquareOutlinedIcon sx={{ fontSize: 18 }} />
              </button>
            )}
            <button
              type='button'
              onClick={closeInitialBalanceModal}
              aria-label='Cerrar modal'
              title='Cerrar'
              className='inline-flex h-7 w-7 items-center justify-center rounded text-surface transition hover:bg-surface/15'
            >
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </button>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto p-4 sm:p-6'>
        <div className='flex flex-col gap-4'>
          <div className='rounded-card border border-border bg-border/30 px-4 py-4 shadow-card'>
            <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
              <div className='flex items-start gap-3'>
                <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/10 bg-surface text-primary shadow-sm'>
                  <LocalAtmOutlinedIcon fontSize='small' />
                </div>
                <div>
                  <h2 className='text-lg sm:text-xl font-semibold text-text'>Saldo Inicial Bancos</h2>
                  <p className='mt-1 max-w-[42rem] text-xs sm:text-sm text-muted'>
                    {initialBalancesLoading
                      ? 'Cargando saldos iniciales...'
                      : hasInitialBalances
                      ? 'Revisa el detalle por banco y administra los registros desde aquí.'
                      : 'Agrega un saldos iniciales a este reporte para mantener el flujo actualizado.'}
                  </p>
                </div>
              </div>

              <div className='flex w-full flex-wrap items-start gap-2 sm:w-auto sm:justify-end'>
                {!initialBalancesLoading && (
                  <div className='min-w-[170px] rounded-2xl border border-border bg-surface px-3 py-3 text-right'>
                    <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted'>
                      Total agregado
                    </p>
                    <p className='text-sm font-semibold text-text'>{formatCLPFull(initialBalanceTotal)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {initialBalancesLoading ? (
            <div className='rounded-card border border-border bg-surface px-4 py-8 text-center text-sm text-muted shadow-card'>
              Cargando saldos iniciales...
            </div>
          ) : hasInitialBalances ? (
            <div className='overflow-hidden rounded-card border border-border bg-surface shadow-card'>
              <div className='hidden sm:grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 border-b border-border px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted'>
                <span>Banco</span>
                <span>Saldo inicial</span>
                <span className='sm:text-center'>Fecha de vigencia</span>
                <span className='text-right'>Acciones</span>
              </div>
              <div className='divide-y divide-border'>
                {initialBalanceRows.map(row => (
                  <div
                    key={row.key}
                    className='grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-center'
                  >
                    <div className='min-w-0'>
                      <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted sm:hidden'>Banco</p>
                      <p className='text-sm font-semibold text-text break-words'>{row.bankName}</p>
                    </div>
                    <div className='min-w-0'>
                      <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted sm:hidden'>Saldo inicial</p>
                      <p className='text-sm font-semibold text-text'>{row.balanceLabel}</p>
                    </div>
                    <div className='min-w-0 sm:text-center'>
                      <p className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted sm:hidden'>Fecha de vigencia</p>
                      <p className='text-sm text-text sm:text-center'>{row.effectiveDateLabel}</p>
                    </div>
                    <div className='sm:justify-self-end'>
                      <div className='flex flex-wrap justify-start gap-2 sm:justify-end'>
                        <button
                          type='button'
                          onClick={() => handleEditInitialBalance(row)}
                          disabled={!row?.id || isSavingInitialBalance || deletingInitialBalanceId === row?.id}
                          aria-label='Editar saldo inicial'
                          title={editingInitialBalanceId === row?.id ? 'Editando saldo inicial' : 'Editar saldo inicial'}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                            editingInitialBalanceId === row?.id
                              ? 'border-accent2/20 bg-accent2/15 text-accent2'
                              : 'border-accent2/20 text-accent2 hover:bg-accent2/10'
                          }`}
                        >
                          <EditOutlinedIcon fontSize='small' />
                        </button>
                        <button
                          type='button'
                          onClick={() => handleDeleteInitialBalance(row)}
                          disabled={
                            !row?.id ||
                            deletingInitialBalanceId === row?.id ||
                            isSavingInitialBalance
                          }
                          aria-label='Eliminar saldo inicial'
                          title={deletingInitialBalanceId === row?.id ? 'Eliminando saldo inicial' : 'Eliminar saldo inicial'}
                          className='inline-flex h-8 w-8 items-center justify-center rounded-full border border-danger/20 text-danger transition hover:bg-danger/10 disabled:opacity-60 disabled:cursor-not-allowed'
                        >
                          <DeleteOutlineIcon fontSize='small' />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className='rounded-card border border-border bg-surface px-4 py-4 shadow-card'>
            <div className='mb-4'>
              <div>
                <h3 className='text-base sm:text-lg font-semibold text-text'>
                  {isEditingInitialBalance ? 'Editar' : 'Agregar'}
                </h3>
              </div>
            </div>

            <form id='initial-balance-form' className='space-y-4' onSubmit={handleSubmitInitialBalance}>
              {isEditingInitialBalance ? (
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <div>
                    <label className='mb-1 block text-xs font-semibold text-text'>Banco</label>
                    <select
                      value={initialBalanceForm.bankId}
                      onChange={event => handleChangeInitialBalanceForm('bankId', event.target.value)}
                      className='w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm'
                      disabled={isSavingInitialBalance || isEditingInitialBalance}
                    >
                      <option value=''>Selecciona un banco</option>
                      {bankCatalog.map(bank => (
                        <option key={bank?.id} value={bank?.id}>
                          {bank?.name}
                        </option>
                      ))}
                    </select>
                    {banksLoading && (
                      <p className='mt-1 text-xs text-muted'>Cargando bancos...</p>
                    )}
                    {isEditingInitialBalance && (
                      <p className='mt-1 text-xs text-muted'>El banco no se puede modificar en edición.</p>
                    )}
                  </div>

                  <div>
                    <label className='mb-1 block text-xs font-semibold text-text'>Saldo inicial</label>
                    <input
                      type='text'
                      inputMode='numeric'
                      value={initialBalanceForm.initialBalance}
                      onChange={event =>
                        handleChangeInitialBalanceForm(
                          'initialBalance',
                          formatInitialBalanceCurrencyInput(event.target.value)
                        )}
                      className='w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm'
                      placeholder='Ej: $ 100.000'
                      disabled={isSavingInitialBalance}
                    />
                  </div>
                </div>
              ) : (
                <div className='space-y-3'>
                  <div className='overflow-hidden rounded-2xl border border-border bg-border/20'>
                    {initialBalanceDrafts.map((draft, index) => {
                      const usedBankIds = new Set(
                        initialBalanceDrafts
                          .filter((_, draftIndex) => draftIndex !== index)
                          .map(item => Number(item.bankId ?? 0))
                          .filter(Boolean)
                      )

                      return (
                        <div
                          key={`initial-balance-draft-${index}`}
                          className={`p-3 sm:p-4 ${index === 0 ? '' : 'border-t border-border'}`}
                        >
                          <div className='grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end'>
                            <div>
                              <label className='mb-1 block text-xs font-semibold text-text'>Banco</label>
                              <select
                                value={draft.bankId}
                                onChange={event =>
                                  handleChangeInitialBalanceDraft(index, 'bankId', event.target.value)}
                                className='w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm'
                                disabled={isSavingInitialBalance}
                              >
                                <option value=''>Selecciona un banco</option>
                                {bankCatalog.map(bank => {
                                  const bankId = Number(bank?.id ?? 0)
                                  return (
                                    <option
                                      key={bank?.id}
                                      value={bank?.id}
                                      disabled={usedBankIds.has(bankId)}
                                    >
                                      {bank?.name}
                                    </option>
                                  )
                                })}
                              </select>
                              {banksLoading && (
                                <p className='mt-1 text-xs text-muted'>Cargando bancos...</p>
                              )}
                            </div>

                            <div>
                              <label className='mb-1 block text-xs font-semibold text-text'>Saldo inicial</label>
                              <input
                                type='text'
                                inputMode='numeric'
                                value={draft.initialBalance}
                                onChange={event =>
                                  handleChangeInitialBalanceDraft(
                                    index,
                                    'initialBalance',
                                    formatInitialBalanceCurrencyInput(event.target.value)
                                  )}
                                className='w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm'
                                placeholder='Ej: $ 100.000'
                                disabled={isSavingInitialBalance}
                              />
                            </div>

                            <div className='flex justify-end sm:pb-[2px]'>
                              {initialBalanceDrafts.length > 1 && (
                                <button
                                  type='button'
                                  onClick={() => handleRemoveInitialBalanceDraft(index)}
                                  disabled={isSavingInitialBalance}
                                  aria-label='Eliminar fila'
                                  title='Eliminar fila'
                                  className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-danger/20 text-danger transition hover:bg-danger/10 disabled:opacity-60'
                                >
                                  <DeleteOutlineIcon fontSize='small' />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className='pt-1'>
                    <button
                      type='button'
                      onClick={handleAddInitialBalanceDraft}
                      disabled={isSavingInitialBalance}
                      className='inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-accent2/90 disabled:opacity-60'
                    >
                      <AddOutlinedIcon fontSize='small' />
                      Agregar otro banco
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className='flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              {isEditingInitialBalance && (
                <button
                  type='button'
                  onClick={handleCancelInitialBalanceEdit}
                  disabled={isSavingInitialBalance}
                  className='inline-flex items-center justify-center rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition hover:bg-border/60 disabled:opacity-60'
                >
                  Cancelar edición
                </button>
              )}
            </div>

            <div className='flex flex-wrap justify-end gap-2'>
              <button
                type='submit'
                form='initial-balance-form'
                disabled={isSavingInitialBalance}
                className='inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-surface shadow-sm transition hover:opacity-90 disabled:opacity-60'
              >
                {isSavingInitialBalance
                  ? isEditingInitialBalance
                    ? 'Guardando cambios...'
                    : initialBalanceDrafts.length > 1
                    ? 'Guardando saldos...'
                    : 'Guardando...'
                  : isEditingInitialBalance
                  ? 'Guardar cambios'
                  : initialBalanceDrafts.length > 1
                  ? 'Guardar saldos iniciales'
                  : 'Guardar saldo inicial'}
              </button>
              <button
                type='button'
                onClick={closeInitialBalanceModal}
                className='inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-surface shadow-sm transition hover:opacity-90'
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </Dialog>
  )
}
